import { datadogRum } from '@datadog/browser-rum';
import type { ReactElement, VoidFunctionComponent } from 'react';
import type { InjectedRouter, RouterState } from 'react-router';
import type { Params } from 'react-router/lib/Router';

import ApplicationMobileWarning from './components/ApplicationMobileWarning';
import CommandPaletteProvider from './components/CommandPalette';
import DialogManager from './components/DialogManager';
import Dresser from './components/Dresser/Dresser';
import StatusBanner from './components/generic/StatusBanner';
import JobNotificationProvider from './components/JobNotificationHub/Provider';
import { Provider as PortalUrlProvider } from './components/shared/publicPortal/context/portalUrlContext';
import { SurveyProvider } from './components/surveys/SurveyProvider';
import Toaster from './components/Toaster/Toaster';
import TrialEndSurveyModal from './components/trialEndSurvey/TrialEndSurveyModal';
import DialogContextProvider from './contexts/DialogContext';
import { DrawerContextProvider } from './contexts/DrawerContext';
import PlanSelectorContextProvider, { usePlanSelectorContext } from './contexts/PlanSelectorContext';
import RouterContextProvider from './contexts/RouterContext';
import ToastContextProvider from './contexts/ToastContext';
import { window } from './globals';
import { useOnMount, useOnUnmount } from './hooks';
import * as utils from './utils';
import { instrumentFetch } from './utils/tracing';
import type { MessageOptions } from './components/settings/billing/features/consts';

type ApplicationProps = {
  children: ReactElement;
  location: RouterState['location'];
  router: InjectedRouter & { params: Params};
};

const enableDatadogRUM = () => {
  if (!utils.isTrue(window.datadogRUM?.enabled)) {
    return;
  }
  instrumentFetch(); // this must be called before datadogRum.init
  datadogRum.init({
    version: window.fossa_version,
    ...window.datadogRUM,
  });

  if (window.user) {
    datadogRum.setUser({
      id: `${window.user.id}`,
      name: window.user.username,
      email: window.user.email,
      organizationId: window.user.organizationId,
      organizationTitle: window.user.organization?.title,
    });
  }
};

const enableDevrev = () => {
  if (!window.devrev.appId || !window.plugSDK) {
    return;
  }

  window.plugSDK.init({
    app_id: window.devrev.appId,
    session_token: window.devrev.sessionToken,
  });
};

type PlansModalContextWrapperProps = {
  children: ReactElement;
};

const PlansModalContextWrapper: VoidFunctionComponent<PlansModalContextWrapperProps> = ({
  children,
}: PlansModalContextWrapperProps) => {
  const { showPlanSelectorModal } = usePlanSelectorContext();

  useOnMount(() => {
    window.showPlanSelectorModal = (messageOptions?: MessageOptions) => showPlanSelectorModal(messageOptions);
  });

  useOnUnmount(() => {
    // @ts-expect-error - TODO - remove this global state - CORE-4179
    delete window.showPlanSelectorModal;
  });

  return children;
};

const Application: VoidFunctionComponent<ApplicationProps> = ({
  children,
  location,
  router,
}: ApplicationProps) => {
  useOnMount(() => {
    enableDatadogRUM();
    enableDevrev();
    utils.showDelightedSurveyDefault();
  });

  return (
    <PortalUrlProvider>
      <DialogContextProvider>
        <ToastContextProvider>
          <RouterContextProvider router={router} location={location}>
            <DrawerContextProvider locationBasename={location.pathname}>
              <JobNotificationProvider>
                <CommandPaletteProvider>
                  {utils.isAuthed() && <ApplicationMobileWarning />}
                  <StatusBanner />
                  <PlanSelectorContextProvider>
                    <SurveyProvider>
                      <PlansModalContextWrapper>{children}</PlansModalContextWrapper>
                    </SurveyProvider>
                    <TrialEndSurveyModal />
                  </PlanSelectorContextProvider>
                  <DialogManager />
                  <Toaster location={location} />
                  <Dresser />
                </CommandPaletteProvider>
              </JobNotificationProvider>
            </DrawerContextProvider>
          </RouterContextProvider>
        </ToastContextProvider>
      </DialogContextProvider>
    </PortalUrlProvider>
  );
};

export default Application;
